import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import { BRAND } from "@/lib/constants";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-white text-[#111111] relative overflow-hidden border-t border-black/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <Link to="/" className="flex items-center gap-5 mb-8">
              <img
                src={BRAND.logoUrl}
                alt="Gudiksen Stays"
                className="h-14 w-14 object-contain"
              />
              <div>
                <div
                  className="font-serif text-2xl tracking-[0.2em] text-[#111111]"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  GUDIKSEN
                </div>
                <div className="text-[10px] tracking-[0.4em] text-[#C9A227] mt-1">
                  - STAYS -
                </div>
              </div>
            </Link>

            <h3
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#111111] font-light leading-[1] tracking-tight"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Mere tid.
              <br />
              <span className="italic text-[#C9A227]">Mindre drift.</span>
            </h3>

            {/* Social visitkort */}
            <div className="mt-10 inline-flex items-center gap-4 border border-black/10 bg-[#F8F8F8] px-5 py-4">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#C9A227] mr-2">
                Følg os
              </div>
              <a
                href={BRAND.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="w-9 h-9 rounded-full border border-[#111111]/15 flex items-center justify-center hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-white text-[#111111] transition-colors"
              >
                <Facebook size={15} strokeWidth={1.5} />
              </a>
              <a
                href={BRAND.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="w-9 h-9 rounded-full border border-[#111111]/15 flex items-center justify-center hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-white text-[#111111] transition-colors"
              >
                <Instagram size={15} strokeWidth={1.5} />
              </a>
              <a
                href={BRAND.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="social-linkedin"
                className="w-9 h-9 rounded-full border border-[#111111]/15 flex items-center justify-center hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-white text-[#111111] transition-colors"
              >
                <Linkedin size={15} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-10">
            <div>
              <div className="overline mb-5" style={{ color: "#C9A227" }}>
                Navigation
              </div>
              <ul className="space-y-3 text-[#222222] font-light text-sm">
                <li>
                  <HashLink smooth to="/#services" className="link-underline">
                    Services
                  </HashLink>
                </li>
                <li>
                  <HashLink smooth to="/#why" className="link-underline">
                    Hvorfor os
                  </HashLink>
                </li>
                <li>
                  <HashLink smooth to="/#process" className="link-underline">
                    Sådan starter du
                  </HashLink>
                </li>
                <li>
                  <HashLink smooth to="/#pricing" className="link-underline">
                    Pris
                  </HashLink>
                </li>
                <li>
                  <Link to="/cases" className="link-underline inline-flex items-center gap-2">
                    Cases
                    <span className="text-[8px] tracking-[0.25em] uppercase px-1 py-0.5 border border-[#C9A227]/60 text-[#C9A227]">
                      Ny
                    </span>
                  </Link>
                </li>
                <li>
                  <Link to="/for-gaester" className="link-underline">
                    For gæster
                  </Link>
                </li>
                <li>
                  <Link to="/om-os" className="link-underline">
                    Om os
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="link-underline">
                    FAQ
                  </Link>
                </li>
                <li>
                  <HashLink smooth to="/#contact" className="link-underline">
                    Kontakt
                  </HashLink>
                </li>
              </ul>
            </div>

            <div>
              <div className="overline mb-5" style={{ color: "#C9A227" }}>
                Kontakt
              </div>
              <ul className="space-y-3 text-[#222222] font-light text-sm">
                <li>
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="link-underline break-all"
                    data-testid="footer-email-link"
                  >
                    {BRAND.email}
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:${BRAND.phoneRaw}`}
                    className="link-underline"
                    data-testid="footer-phone-link"
                  >
                    {BRAND.phone}
                  </a>
                </li>
                <li>
                  <a
                    href={BRAND.calendlyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                    data-testid="footer-calendly-link"
                  >
                    Book en uforpligtende samtale
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <div className="overline mb-5" style={{ color: "#C9A227" }}>
                Firma
              </div>
              <address
                className="not-italic space-y-1 text-[#222222] font-light text-sm leading-relaxed"
                data-testid="footer-legal-info"
              >
                <div className="text-[#111111] font-normal">
                  {BRAND.legalName}
                </div>
                <div>CVR {BRAND.cvr}</div>
                <div>{BRAND.address.street}</div>
                <div>{BRAND.address.city}</div>
                <div>{BRAND.address.country}</div>
              </address>
              <ul className="mt-5 space-y-3 text-[#222222] font-light text-sm">
                <li>
                  <Link to="/privatlivspolitik" className="link-underline">
                    Privatlivspolitik
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="link-underline">
                    Cookiepolitik
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-black/10 flex flex-col sm:flex-row justify-between gap-3 text-xs tracking-wider text-[#555555]">
          <div>
            © {new Date().getFullYear()} {BRAND.legalName} · CVR {BRAND.cvr}. Alle rettigheder forbeholdes.
          </div>
          <div className="uppercase tracking-[0.25em] text-[#C9A227]">
            Premium Property Management · Danmark
          </div>
        </div>
      </div>
    </footer>
  );
}
